/**
 * Portal shell — minimal, partner-focused. Top bar with brand + horizontal
 * nav + sign-out. No admin sidebar; partners only see what's theirs.
 *
 * Sprint 1 only ships /dashboard so the nav is single-item; later sprints
 * add Referrals, Payouts, Marketing kit. Keeping the chrome thin for now
 * means the dashboard hero gets to do the talking.
 */

import { type ReactNode, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";

import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import LaptopIcon from "@mui/icons-material/LaptopMacOutlined";

import { useAuth } from "../../../shared/providers/AuthProvider";
import { useColorMode } from "../../../shared/providers/ColorModeProvider";
import { tokens } from "../../../lib/theme";
import { adminPortalUrl, isPartnerHost } from "../../../lib/hosts";
import { useI18n } from "../../../shared/providers/I18nProvider";
import { usePartner } from "./PortalGate";

interface PortalNavItem {
  /** Path *without* the `/portal` prefix — gets prefixed at render time
   *  based on which host we're on. */
  slug: string;
  labelKey: string;
}

const NAV: PortalNavItem[] = [
  { slug: "/dashboard",     labelKey: "portal.shell.nav.dashboard" },
  { slug: "/referrals",     labelKey: "portal.shell.nav.referrals" },
  { slug: "/payouts",       labelKey: "portal.shell.nav.payouts" },
  { slug: "/marketing-kit", labelKey: "portal.shell.nav.marketingKit" },
  { slug: "/kyc",           labelKey: "portal.shell.nav.verification" },
];

/** On partners.* the portal lives at the root (`/dashboard`); on
 *  engage.* / dev it stays under `/portal/*` for back-compat. */
function portalPath(slug: string): string {
  return isPartnerHost() ? slug : `/portal${slug}`;
}

export function PortalShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const theme = useTheme();
  const { pathname } = useLocation();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // Subtle mint-tinted background so the surface itself reads
        // "different app" before you even look at the chrome.
        bgcolor: isDark ? "background.default" : alpha(tokens.accent.mint, 0.04),
      }}
    >
      {/* Partner-portal accent ribbon — visible across every page. */}
      <Box
        sx={{
          height: 3,
          background: `linear-gradient(90deg, ${tokens.accent.mintDeep} 0%, ${tokens.accent.mint} 50%, ${tokens.brand.purple} 100%)`,
        }}
      />

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 3,  // sit below the ribbon
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: "saturate(180%) blur(12px)",
        }}
      >
        <Toolbar sx={{ minHeight: 60, gap: { xs: 1, md: 2 }, px: { xs: 2, md: 4 } }}>
          <Brand />
          <PortalViewBadge t={t} />
          <Stack direction="row" spacing={0.5} sx={{ ml: 3, display: { xs: "none", md: "flex" } }}>
            {NAV.map((item) => {
              const to = portalPath(item.slug);
              const active = pathname.startsWith(to);
              return (
                <Box
                  key={item.slug}
                  component={RouterLink}
                  to={to}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    fontSize: 13.5,
                    fontWeight: active ? 600 : 500,
                    color: active ? tokens.accent.mintDeep : "text.secondary",
                    textDecoration: "none",
                    bgcolor: active ? alpha(tokens.accent.mint, 0.18) : "transparent",
                    transition: "background-color 120ms ease",
                    "&:hover": {
                      bgcolor: alpha(tokens.accent.mint, active ? 0.24 : 0.1),
                    },
                  }}
                >
                  {t(item.labelKey)}
                </Box>
              );
            })}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <ColorModeButton />
          <PartnerMenu />
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 5 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function PortalViewBadge({ t }: { t: (key: string) => string }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: { xs: "none", sm: "inline-flex" },
        alignItems: "center",
        gap: 0.6,
        ml: 1.5,
        px: 1,
        py: 0.4,
        borderRadius: 1,
        border: `1px solid ${alpha(tokens.accent.mintDeep, 0.4)}`,
        bgcolor: alpha(tokens.accent.mint, theme.palette.mode === "dark" ? 0.15 : 0.12),
        color: tokens.accent.mintDeep,
        fontFamily: tokens.font.mono,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        lineHeight: 1,
      }}
    >
      <Box
        sx={{
          width: 6, height: 6, borderRadius: "50%",
          bgcolor: tokens.accent.mintDeep,
          boxShadow: `0 0 0 3px ${alpha(tokens.accent.mint, 0.3)}`,
        }}
      />
      {t("portal.shell.partnerView")}
    </Box>
  );
}

function Brand() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      {/* Gradient P mark from frontend/public/paybyrd-mark.svg. */}
      <Box
        component="img"
        src="/paybyrd-mark.svg"
        alt="Paybyrd"
        sx={{ height: 24, width: "auto", display: "block" }}
      />
      <Typography
        sx={{
          fontFamily: tokens.font.display,
          fontWeight: 600,
          fontSize: 16,
          letterSpacing: "-0.015em",
          lineHeight: 1.1,
          color: theme.palette.text.primary,
        }}
      >
        <Box component="span" sx={{ color: isDark ? "rgba(255,255,255,0.55)" : "rgba(15,10,31,0.55)" }}>
          Engage
        </Box>{" "}
        <Box component="span" sx={{ color: "primary.main" }}>Partners</Box>
      </Typography>
    </Stack>
  );
}

function ColorModeButton() {
  const { t } = useI18n();
  const { preference, toggle } = useColorMode();
  const Icon = preference === "light" ? LightModeIcon : preference === "dark" ? DarkModeIcon : LaptopIcon;
  const label = preference === "system" ? t("portal.shell.theme.system") : preference === "dark" ? t("portal.shell.theme.dark") : t("portal.shell.theme.light");
  return (
    <Tooltip title={`${label} ${t("portal.shell.theme.cycleHint")}`}>
      <IconButton size="small" onClick={toggle} aria-label={t("portal.shell.theme.cycleAria")}>
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function PartnerMenu() {
  const { t } = useI18n();
  const partner = usePartner();
  const { logout } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  // Admin-ness is resolved server-side from the verified token (PartnerProfile.is_admin),
  // not inferred from the email domain. This is a UI affordance only — admin routes are
  // gated by the backend's require_engage_admin regardless of what we render here.
  const isAlsoAdmin = partner.is_admin;

  const initials = partner.display_name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "P";

  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          textTransform: "none",
          color: "text.primary",
          gap: 1,
          pr: 1,
          pl: 0.5,
          py: 0.5,
          borderRadius: 999,
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${tokens.brand.purpleDeep} 0%, ${tokens.brand.purple} 100%)`,
            color: "#fff",
            fontFamily: tokens.font.display,
            fontWeight: 600,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            letterSpacing: "0.02em",
          }}
        >
          {initials}
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" }, fontSize: 13.5, fontWeight: 500 }}>
          {partner.display_name}
        </Box>
      </Button>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1.5, minWidth: 240 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{partner.display_name}</Typography>
          <Typography variant="caption" color="text.secondary">{partner.email}</Typography>
        </Box>
        {isAlsoAdmin && (
          <MenuItem
            component="a"
            href={adminPortalUrl("/overview")}
            onClick={() => setAnchor(null)}
            sx={{ fontSize: 13.5 }}
          >
            {t("portal.shell.openAdminPortal")}
          </MenuItem>
        )}
        <MenuItem onClick={() => { setAnchor(null); logout(); }} sx={{ fontSize: 13.5 }}>
          {t("portal.shell.signOut")}
        </MenuItem>
      </Menu>
    </>
  );
}
